import React, { useCallback, useMemo, useRef } from 'react';

import config from 'config';
import { useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import {
  desktop,
  mobile,
  SkeletonLine,
  useBreakpoint,
} from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';
import {
  ProductListViewedProvider,
  useProductListing,
} from '@techstyle/react-products';
// eslint-disable-next-line node/no-unpublished-import
import type { CatalogProductFilterDto } from '@techstyle/react-types';

import { Header } from '../../styles/header';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import {
  ShoppingBagFlagStatusType,
  useLDNewShoppingBagUX,
} from '../../utils/LD/useNewShoppingBagUX';
import { useSegmentedValue } from '../../utils/segmentation';
import {
  getDefaultProductFields,
  getDefaultProductFilters,
} from '../../utils/selectors';
import { RootState } from '../../utils/types';
import { Component as Carousel } from '../Carousel';
import BaseProductCellBMIG from '../ProductCellBMIG';
import { useQuickViewContext, ADD_TO_CART_UPSELL } from '../QuickViewProvider';

import { LeftArrow, RightArrow } from './ArrowIcon';

const setId = config.get('public.productTypes.setId');
const CART_UPSELL_SOURCE = 'shopping_bag_upsell';

const defaultOptions = {
  includeOutOfStock: false,
  autoExcludeClearanceFplIds: false,
};

const defaultFilters = {
  size: 10,
  productFields: getDefaultProductFields(),
};

const productCellElementsToHide = {
  price: false,
  colorsAvailable: true,
  flags: true,
  reviewStars: true,
  promo: true,
  wishlist: true,
  name: false,
};

const defaultCarouselOptions = {
  slidesToShow: 2.25,
  slidesToScroll: 2,
  arrows: false,
  dots: false,
};

const Wrapper = styled.div<{
  shoppingBagFlagStatus?: ShoppingBagFlagStatusType;
  isVariantRevampedSB?: boolean;
}>`
  display: grid;
  grid-template-areas:
    'topArea'
    'carousel';
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  row-gap: 16px;

  ${mobile`
    padding: ${props => `${props.theme.spacing.medium}px`};

    ${props =>
      props.shoppingBagFlagStatus?.isControl &&
      `
        border-bottom: 1px solid ${props => props.theme.colors.lavender200};
        border-top: 1px solid ${props.theme.colors.lavender200};
      `}

    ${props =>
      props.isVariantRevampedSB && `padding: ${props.theme.spacing.small}px;`}
  `}

  ${desktop`
    padding: ${props =>
      props.isVariantRevampedSB
        ? props.theme.spacing.small
        : props.theme.spacing.medium}px;
    padding-right: 0;
    background: ${props => props.theme.colors.white};
    border-radius 8px;
  `}
`;

const TopArea = styled.div<{
  isVariantRevampedSB?: boolean;
}>`
  grid-area: topArea;
  display: grid;
  padding: 0 16px;

  ${mobile`
    justify-content: left;
  `}

  ${desktop`
    grid-template-areas: 'title arrows';
    grid-template-columns: auto minmax(0, 60px);
  `}

  
  padding: 0 8px;
  ${props =>
    props.isVariantRevampedSB &&
    `
      padding: 0 8px 0 0;
    `}
`;

const ProductCellBMIG = styled(BaseProductCellBMIG)`
  padding: 0;
  border-radius: 4px;
  overflow: hidden;

  border-radius: 0;

  ${BaseProductCellBMIG.ProductSection} {
    margin: 8px 0 0;
  }
`;

const Title = styled(Header).attrs({ level: 3 })`
  grid-area: title;
  font-size: 16px;
  font-weight: 700;
  line-height: calc(19 / 16);

  ${mobile`
    text-align: left;
  `}

  ${props =>
    props.isVariantRevampedSB
      ? props.theme.header.variants.h3.textStyles
      : props.theme.subHeader.variants.subHeading3Uppercase.textStyles}
`;

const CarouselWrapper = styled.div<{
  isVariantRevampedSB?: boolean;
}>`
  grid-area: carousel;

  .slick-track {
    display: flex;
    gap: 8px;
  }

  ${props =>
    props.isVariantRevampedSB
      ? props.theme.header.variants.h3.textStyles
      : props.theme.subHeader.variants.subHeading3Uppercase.textStyles}

  ${mobile`
    ${props =>
      props.isVariantRevampedSB &&
      `
      .slick-list {
            margin-right: -${props.theme.spacing.tiny}px;
            margin-left: -${props.theme.spacing.tiny}px;
          }
      `}
    `}
  ${desktop`
    ${props =>
      props.isVariantRevampedSB &&
      `
      .slick-list {
          margin-right: ${props.theme.spacing.small}px;
          margin-left: -${props.theme.spacing.tiny}px;
        }
    `}
  `}
`;

const ArrowsArea = styled.div`
  grid-area: arrows;
  display: flex;
  justify-content: space-between;
  align-items: center;

  button {
    cursor: pointer;
    line-height: 1;

    &:disabled {
      color: ${props => props.theme.colors.gray200};
      cursor: not-allowed;
    }
  }

  gap: 12px;
`;

const StyledSkeletonLine = styled(SkeletonLine)`
  aspect-ratio: 3/5;
`;

type PartialCarouselOptions = {
  slidesToShow?: number;
  slidesToScroll?: number;
  arrows?: boolean;
  dots?: boolean;
  afterChange?: (next: number) => void;
  beforeChange?: (prev: number, next: number) => void;
};

type CartUpsellCarouselProps = {
  carouselOptions?: PartialCarouselOptions;
};

type PartialCarouselMethods = {
  slickNext: () => void;
  slickPrev: () => void;
};

function CartUpsellCarousel({
  carouselOptions: carouselOptionsFromProps = defaultCarouselOptions,
}: CartUpsellCarouselProps) {
  const { isMobile } = useBreakpoint();
  const shoppingBagFlagStatus = useLDNewShoppingBagUX();
  const { context } = useTheme();
  const { 'upsell-carousel-in-cart': upsellCarouselInCart } = useLDFlags();
  const extraFilterOptions =
    useSegmentedValue<CatalogProductFilterDto>(upsellCarouselInCart);
  const fplId = useMemo(() => {
    if (extraFilterOptions?.backgroundFplIds) {
      if (extraFilterOptions.backgroundFplIds.length === 1) {
        return extraFilterOptions.backgroundFplIds[0];
      }
    }

    return null;
  }, [extraFilterOptions]);

  const isVariantRevampedSB = useMemo(
    () => context?.cartDrawer && shoppingBagFlagStatus?.isVariant,
    [context.cartDrawer, shoppingBagFlagStatus.isVariant]
  );

  const completeFilterOptions = useMemo(() => {
    const options = {
      ...defaultFilters,
      ...(extraFilterOptions ?? {}),
    };

    if (extraFilterOptions?.backgroundFplIds) {
      options.sort = 'fplasc';
    }

    return options;
  }, [extraFilterOptions]);
  const [carouselIndex, setCarouselIndex] = React.useState(0);
  const carouselRef = useRef<null | PartialCarouselMethods>(null);
  const params = useSelector<RootState>(state =>
    getDefaultProductFilters(state, completeFilterOptions, defaultOptions)
  );
  const { openQuickViewModal, setDefaultProductWithSelections } =
    useQuickViewContext();
  const isDraggingRef = useRef(false);

  const getOpenQuickView = useCallback(
    (product, isRegularSet) => async () => {
      if (!isDraggingRef.current) {
        setDefaultProductWithSelections(product, isRegularSet, true);
        openQuickViewModal({
          cartInteraction: ADD_TO_CART_UPSELL,
          source: CART_UPSELL_SOURCE,
        });
      }
    },
    [openQuickViewModal, setDefaultProductWithSelections]
  );

  const { products = [] } = useProductListing(params, {
    apiVersion: 2,
  });

  const handleNext = useCallback(() => {
    if (carouselRef.current) {
      carouselRef.current.slickNext();
    }
  }, []);

  const handlePrev = useCallback(() => {
    if (carouselRef.current) {
      carouselRef.current.slickPrev();
    }
  }, []);

  const handleBeforeChange = useCallback(
    (prev: number, next: number) => {
      isDraggingRef.current = true;

      if (carouselOptionsFromProps.beforeChange) {
        carouselOptionsFromProps.beforeChange(prev, next);
      }

      setCarouselIndex(next);
    },
    [carouselOptionsFromProps]
  );

  const handleAfterChange = useCallback(
    (next: number) => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
      }

      if (carouselOptionsFromProps.afterChange) {
        carouselOptionsFromProps.afterChange(next);
      }
    },
    [carouselOptionsFromProps]
  );

  const carouselOptions = useMemo(
    () => ({
      ...carouselOptionsFromProps,
      afterChange: handleAfterChange,
      beforeChange: handleBeforeChange,
    }),
    [carouselOptionsFromProps, handleAfterChange, handleBeforeChange]
  );

  const prevDisabled = carouselIndex === 0;
  const nextDisabled =
    products.length === carouselIndex + (carouselOptions.slidesToShow ?? 0);

  return (
    <Wrapper
      shoppingBagFlagStatus={shoppingBagFlagStatus}
      isVariantRevampedSB={isVariantRevampedSB}
    >
      <TopArea isVariantRevampedSB={isVariantRevampedSB}>
        <Title isVariantRevampedSB={isVariantRevampedSB}>
          <FormattedMessage
            id="global_checkout.upsell_carousel_title"
            defaultMessage="You May Also Like"
          />
        </Title>
        {!isMobile && (
          <ArrowsArea>
            <button onClick={handlePrev} disabled={prevDisabled} type="button">
              <LeftArrow disabled={prevDisabled} />
            </button>
            <button onClick={handleNext} disabled={nextDisabled} type="button">
              <RightArrow disabled={nextDisabled} />
            </button>
          </ArrowsArea>
        )}
      </TopArea>
      <CarouselWrapper isVariantRevampedSB={isVariantRevampedSB}>
        <ProductListViewedProvider>
          {/* @ts-ignore inferred types from `Carousel` are incorrect */}
          <Carousel
            sliderRef={carouselRef}
            products={products}
            options={carouselOptions}
          >
            {products.length
              ? products.map((product, index) => (
                  <ProductCellBMIG
                    key={product.masterProductId}
                    // @ts-ignore inferred types from `ProductCellBMIG` are incorrect
                    product={product}
                    isQuickViewEnabled
                    setDefaultProductAndOpenQuickViewModal={getOpenQuickView(
                      product,
                      setId === product.productTypeId
                    )}
                    elementsToHide={productCellElementsToHide}
                    gridIndex={index + 1}
                    productSource={CART_UPSELL_SOURCE}
                    fplId={fplId}
                    shouldShowLaydown
                  />
                ))
              : Array.from({ length: 4 }).map((_, index) => (
                  <StyledSkeletonLine key={index} />
                ))}
          </Carousel>
        </ProductListViewedProvider>
      </CarouselWrapper>
    </Wrapper>
  );
}

export default CartUpsellCarousel;
